import { useState, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useGlobalSearchState } from "@/lib/global-search-state";
import { motion, AnimatePresence } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Search, X, GripHorizontal, Users, Phone, Mail, Building2,
  ChevronRight, ChevronLeft, ExternalLink, UserCircle, Loader2, Contact, List,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type ContactResult = {
  id: string;
  name: string;
  phoneNumber: string | null;
  email: string | null;
  company: string | null;
};

type ContactList = {
  id: string;
  name: string;
  isDefault?: boolean;
  memberCount?: number;
};

export function GlobalSearchHeaderButton() {
  const { toggle, isOpen } = useGlobalSearchState();

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={toggle}
      className={`relative h-8 w-8 ${isOpen ? "text-primary" : ""}`}
      title="Search"
      aria-label="Open search"
      data-testid="button-global-search-toggle"
    >
      <Search className="h-4 w-4" />
    </Button>
  );
}

export function FloatingGlobalSearch() {
  const { isOpen, close } = useGlobalSearchState();
  const dragConstraintsRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [view, setView] = useState<"menu" | "contacts">("menu");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchScope, setSearchScope] = useState<string>("all");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  const { data: contactLists } = useQuery<ContactList[]>({
    queryKey: ["/api/contact-lists"],
    enabled: isOpen,
  });
  const selectedListName = searchScope !== "all" ? contactLists?.find((l) => l.id === searchScope)?.name : null;

  useEffect(() => {
    if (!searchQuery) {
      setDebouncedQuery("");
      return;
    }
    const timer = setTimeout(() => setDebouncedQuery(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    if (isOpen && view === "contacts") {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isOpen, view]);

  const handleBack = () => {
    setView("menu");
    setSearchQuery("");
    setDebouncedQuery("");
    setSearchScope("all");
  };

  const handleClose = () => {
    close();
    setView("menu");
    setSearchQuery("");
    setDebouncedQuery("");
    setSearchScope("all");
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  return (
    <>
      <div ref={dragConstraintsRef as any} className="fixed inset-0 pointer-events-none z-40" />
      <AnimatePresence>
        {isOpen && (
          <motion.div
            drag
            dragConstraints={dragConstraintsRef}
            dragMomentum={false}
            dragElastic={0}
            onDragStart={() => setIsDragging(true)}
            onDragEnd={() => setTimeout(() => setIsDragging(false), 50)}
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            className="fixed top-14 right-4 z-50"
            style={{ touchAction: "none" }}
          >
            <div
              className="w-[520px] rounded-lg shadow-2xl border bg-card text-card-foreground overflow-hidden flex flex-col"
              role="dialog"
              aria-label="Quick Search"
              data-testid="floating-global-search"
            >
              <div
                className="flex items-center justify-between p-3 border-b bg-muted/50 cursor-grab active:cursor-grabbing select-none"
              >
                <div className="flex items-center gap-1.5">
                  <GripHorizontal className="h-4 w-4 text-muted-foreground mr-1" />
                  <button
                    className="font-semibold text-sm hover:text-primary transition-colors"
                    onClick={(e) => { if (!isDragging) handleBack(); }}
                    data-testid="breadcrumb-search"
                  >
                    Search
                  </button>
                  {view === "contacts" && (
                    <>
                      <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                      <span
                        className={`font-semibold text-sm ${selectedListName ? "hover:text-primary transition-colors cursor-pointer" : "text-primary"}`}
                        onClick={selectedListName ? (e) => { if (!isDragging) setSearchScope("all"); } : undefined}
                        data-testid="breadcrumb-contacts"
                      >
                        Contacts
                      </span>
                      {selectedListName && (
                        <>
                          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="font-semibold text-sm text-primary truncate max-w-[140px]" data-testid="breadcrumb-list-name">
                            {selectedListName}
                          </span>
                        </>
                      )}
                    </>
                  )}
                </div>
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={handleClose} aria-label="Close search" data-testid="button-search-close">
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {view === "menu" && <SearchMenu onSelect={(v) => setView(v as any)} />}
              {view === "contacts" && (
                <ContactSearch
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  debouncedQuery={debouncedQuery}
                  searchScope={searchScope}
                  setSearchScope={setSearchScope}
                  searchInputRef={searchInputRef}
                  onClose={handleClose}
                />
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function SearchMenu({ onSelect }: { onSelect: (view: string) => void }) {
  return (
    <div className="p-3" data-testid="search-menu">
      <p className="text-xs text-muted-foreground mb-3">Select a search type</p>
      <div className="grid grid-cols-2 gap-2">
        <button
          className="flex items-start gap-3 p-3 rounded-lg border border-border hover:border-primary hover:bg-accent transition-colors text-left"
          onClick={() => onSelect("contacts")}
          data-testid="button-search-contacts"
        >
          <div className="h-8 w-8 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Contact className="h-4 w-4 text-primary" />
          </div>
          <div>
            <p className="font-medium text-sm">Search Contacts</p>
            <p className="text-xs text-muted-foreground mt-0.5">Find contacts by name, phone, or email</p>
          </div>
        </button>

        <div className="flex items-start gap-3 p-3 rounded-lg border border-border opacity-50 cursor-not-allowed text-left">
          <div className="h-8 w-8 rounded-md bg-muted flex items-center justify-center flex-shrink-0 mt-0.5">
            <Search className="h-4 w-4 text-muted-foreground" />
          </div>
          <div>
            <p className="font-medium text-sm">More Coming Soon</p>
            <p className="text-xs text-muted-foreground mt-0.5">Additional search options in development</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function ContactSearch({
  searchQuery,
  setSearchQuery,
  debouncedQuery,
  searchScope,
  setSearchScope,
  searchInputRef,
  onClose,
}: {
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  debouncedQuery: string;
  searchScope: string;
  setSearchScope: (s: string) => void;
  searchInputRef: React.RefObject<HTMLInputElement | null>;
  onClose: () => void;
}) {
  const PAGE_SIZE = 25;
  const [currentPage, setCurrentPage] = useState(0);
  const [listPageCursors, setListPageCursors] = useState<Record<number, string | null>>({ 0: null });

  const { data: contactLists } = useQuery<ContactList[]>({
    queryKey: ["/api/contact-lists"],
  });

  const isListSearch = searchScope !== "all";
  const hasSearchQuery = debouncedQuery.length >= 2;
  const selectedList = contactLists?.find((l) => l.id === searchScope);
  const isGeneralList = isListSearch && selectedList?.isDefault === true;
  const shouldFetch = isListSearch ? (isGeneralList ? hasSearchQuery : true) : hasSearchQuery;

  const buildUrl = (page: number) => {
    const searchParam = hasSearchQuery ? `&search=${encodeURIComponent(debouncedQuery)}` : "";
    if (isListSearch) {
      const cursor = listPageCursors[page] ?? null;
      const paginationParam = cursor
        ? `&cursor=${encodeURIComponent(cursor)}`
        : page > 0 ? `&offset=${page * PAGE_SIZE}` : "";
      return `/api/contact-lists/${searchScope}/contacts?limit=${PAGE_SIZE}${paginationParam}${searchParam}`;
    }
    return `/api/contacts?search=${encodeURIComponent(debouncedQuery)}&limit=${PAGE_SIZE}&page=${page + 1}`;
  };

  const { data: pageData, isLoading, isFetching } = useQuery<{ contacts: ContactResult[]; total: number; nextCursor: string | null }>({
    queryKey: isListSearch
      ? ["/api/contact-lists", searchScope, "contacts", debouncedQuery, currentPage]
      : ["/api/contacts", debouncedQuery, currentPage],
    queryFn: async () => {
      const res = await fetch(buildUrl(currentPage), { credentials: "include" });
      if (!res.ok) throw new Error("Search failed");
      const json = await res.json();
      if (Array.isArray(json)) {
        return { contacts: json, total: json.length, nextCursor: null };
      }
      const contacts = json.data ?? json.contacts ?? [];
      const total = json.total ?? contacts.length;
      const nextCursor = typeof json.nextCursor === "string" ? json.nextCursor : null;
      return { contacts, total, nextCursor };
    },
    enabled: shouldFetch,
  });

  useEffect(() => {
    setCurrentPage(0);
  }, [searchScope, debouncedQuery]);

  const contacts: ContactResult[] = pageData?.contacts ?? [];
  const totalCount = pageData?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const hasPrev = currentPage > 0;
  const hasNext = (currentPage + 1) * PAGE_SIZE < totalCount;


  return (
    <div data-testid="contact-search-panel">
      <div className="p-3 space-y-2 border-b">
        <div className="flex items-center gap-2">
          <Select value={searchScope} onValueChange={setSearchScope}>
            <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="select-search-scope">
              <SelectValue placeholder="Search in..." />
            </SelectTrigger>
            <SelectContent style={{ zIndex: 10000 }}>
              <SelectItem value="all" data-testid="option-all-contacts">
                <div className="flex items-center gap-1.5">
                  <Users className="h-3 w-3" />
                  All Contacts
                </div>
              </SelectItem>
              {contactLists?.map((list) => (
                <SelectItem key={list.id} value={list.id} data-testid={`option-list-${list.id}`}>
                  <div className="flex items-center gap-1.5">
                    <List className="h-3 w-3" />
                    {list.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="relative flex-1">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              ref={searchInputRef as any}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Name, phone, email..."
              className="pl-7 h-8 text-sm"
              data-testid="input-contact-search"
            />
          </div>
        </div>
      </div>

      <ScrollArea className="h-[320px]">
        {!shouldFetch ? (
          <div className="flex flex-col items-center justify-center h-[280px] text-muted-foreground">
            <Search className="h-8 w-8 mb-2 opacity-40" />
            <p className="text-sm">
              {isGeneralList
                ? "Type at least 2 characters to search General list"
                : "Type at least 2 characters to search"}
            </p>
          </div>
        ) : (isLoading && contacts.length === 0) ? (
          <div className="flex items-center justify-center h-[280px]">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : contacts.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-[280px] text-muted-foreground">
            <Users className="h-8 w-8 mb-2 opacity-40" />
            <p className="text-sm">No contacts found</p>
          </div>
        ) : (
          <div className="p-1">
            {contacts.map((contact) => (
              <div
                key={contact.id}
                className="flex items-center gap-2 p-2.5 rounded-md hover:bg-accent transition-colors"
                data-testid={`contact-result-${contact.id}`}
              >
                <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <UserCircle className="h-5 w-5 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm truncate" data-testid={`text-contact-name-${contact.id}`}>
                    {contact.name || "Unnamed"}
                  </p>
                  <div className="flex items-center gap-3 mt-0.5">
                    {contact.phoneNumber && (
                      <span className="flex items-center gap-1 text-xs text-muted-foreground truncate" data-testid={`text-contact-phone-${contact.id}`}>
                        <Phone className="h-3 w-3 flex-shrink-0" />
                        {contact.phoneNumber}
                      </span>
                    )}
                    {contact.email && (
                      <span className="flex items-center gap-1 text-xs text-muted-foreground truncate" data-testid={`text-contact-email-${contact.id}`}>
                        <Mail className="h-3 w-3 flex-shrink-0" />
                        {contact.email}
                      </span>
                    )}
                  </div>
                  {contact.company && (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5" data-testid={`text-contact-company-${contact.id}`}>
                      <Building2 className="h-3 w-3 flex-shrink-0" />
                      {contact.company}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    title="Open in new tab"
                    aria-label={`Open ${contact.name || "contact"} in new tab`}
                    onClick={() => window.open(`/contacts/${contact.id}`, "_blank")}
                    data-testid={`button-open-new-tab-${contact.id}`}
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    title="Open in current tab"
                    aria-label={`Open ${contact.name || "contact"} in current tab`}
                    onClick={() => {
                      window.location.href = `/contacts/${contact.id}`;
                      onClose();
                    }}
                    data-testid={`button-open-current-tab-${contact.id}`}
                  >
                    <UserCircle className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
            {totalCount > 0 && (
              <div className="flex items-center justify-between px-3 py-2 border-t mt-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => setCurrentPage(p => p - 1)}
                  disabled={!hasPrev || isFetching}
                  aria-label="Previous page"
                  data-testid="button-page-prev"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-xs text-muted-foreground" data-testid="text-page-info">
                  {isFetching ? (
                    <Loader2 className="h-3 w-3 animate-spin inline mr-1" />
                  ) : null}
                  Page {currentPage + 1} of {totalPages} ({totalCount} contacts)
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => {
                    if (isListSearch) {
                      const nextCursor = pageData?.nextCursor;
                      if (!nextCursor) return;
                      setListPageCursors((currentCursors) => ({
                        ...currentCursors,
                        [currentPage + 1]: nextCursor,
                      }));
                    }
                    setCurrentPage((page) => page + 1);
                  }}
                  disabled={!hasNext || isFetching || (isListSearch && !pageData?.nextCursor)}
                  aria-label="Next page"
                  data-testid="button-page-next"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        )}
      </ScrollArea>

    </div>
  );
}
